import type {
  StatusMovimentacao,
  TipoFluxoMovimentacao,
  MetodoEntrega,
  StatusAcesso,
  TipoEquipamentoMovimentacao,
  SistemaOperacionalMovimentacao,
  PropriedadeEquipamento,
  TipoSolicitacao,
  MotivoExclusaoTipo,
  MotivoExclusaoMovimentacao,
  SolicitacaoAcesso,
} from '../../models';

/** Ver Movimentacao.excluida — mesmo raciocínio de MOTIVO_EXCLUSAO_LABELS (abaixo). */
export const MOTIVO_EXCLUSAO_MOVIMENTACAO_LABELS: Record<MotivoExclusaoMovimentacao, string> = {
  criada_por_engano: 'Criada por engano',
  nao_possui_equipamento: 'Colaborador não possui equipamento',
  equipamento_nao_pertence_raro: 'Equipamento não pertence à Raro',
  duplicada: 'Duplicada (já existe outra Movimentação pra isso)',
  nao_sera_realizada: 'Não será mais realizada',
  outro: 'Outro motivo',
};

/** Opções fechadas (não texto livre) pra manter números de dashboards/relatórios
 * confiáveis mesmo depois de limpar solicitações abertas por engano/bug. */
export const MOTIVO_EXCLUSAO_LABELS: Record<MotivoExclusaoTipo, string> = {
  duplicidade: 'Duplicidade (já existe outra Solicitação pro mesmo assunto)',
  nao_necessaria: 'Não é mais necessária',
  aberta_incorretamente: 'Aberta incorretamente',
  outro: 'Outro motivo',
};

export const STATUS_MOVIMENTACAO_LABELS: Record<StatusMovimentacao, string> = {
  aguardando_preparacao: 'Aguardando Preparação',
  aguardando_postagem_retirada: 'Aguardando Postagem/Retirada',
  em_transito: 'Em Trânsito',
  aguardando_retirada_correios: 'Aguardando Retirada na Agência (Correios)',
  entregue: 'Entregue',
  cancelada: 'Cancelada',
};

export const STATUS_MOVIMENTACAO_BADGE_CLASS: Record<StatusMovimentacao, string> = {
  aguardando_preparacao: 'bg-gray-100 text-gray-700',
  aguardando_postagem_retirada: 'bg-yellow-100 text-yellow-800',
  em_transito: 'bg-blue-100 text-blue-800',
  aguardando_retirada_correios: 'bg-blue-100 text-blue-800',
  entregue: 'bg-green-100 text-green-800',
  cancelada: 'bg-gray-100 text-gray-500',
};

export const TIPO_FLUXO_LABELS: Record<TipoFluxoMovimentacao, string> = {
  envio: 'Envio',
  devolucao: 'Devolução',
};

export const METODO_ENTREGA_LABELS: Record<MetodoEntrega, string> = {
  correios: 'Correios',
  uber: 'Uber',
  pessoalmente: 'Pessoalmente',
  outro: 'Outro',
};

export const STATUS_ACESSO_LABELS: Record<StatusAcesso, string> = {
  pendente: 'Pendente',
  concluido: 'Concluído',
  naoAplicavel: 'Não se aplica',
};

export const STATUS_ACESSO_BADGE_CLASS: Record<StatusAcesso, string> = {
  pendente: 'bg-yellow-100 text-yellow-800',
  concluido: 'bg-green-100 text-green-800',
  naoAplicavel: 'bg-gray-200 text-gray-600',
};

export interface CatalogoAcessoItem {
  nome: string;
  aplicavelEm: TipoSolicitacao[];
  /** Só EMIVE, Cartão de Acesso e Chave Casa têm o estado "Não se aplica" — os demais
   * itens do catálogo são binários (Discovery §19, correção "escopo do não se aplica"). */
  permiteNaoSeAplica: boolean;
}

/**
 * Catálogo fixo de sistemas de acesso (Discovery §19) — hardcoded aqui, mesmo padrão já
 * usado pelos demais catálogos deste arquivo (TIPO_EQUIPAMENTO_LABELS etc.). A tela de
 * administração do catálogo (si_config_catalogo_acesso) não foi construída nesta rodada;
 * se um dia existir, esta constante deveria ser substituída por dados vindos de lá.
 */
export const CATALOGO_ACESSOS: CatalogoAcessoItem[] = [
  { nome: 'Microsoft', aplicavelEm: ['admissao', 'desligamento'], permiteNaoSeAplica: false },
  // Só Desligamento — a empresa não usa mais Google Workspace/G-Suite pra criação (só
  // RaroHub/HelpDesk), mas quem entrou antes da migração pode ainda ter esse acesso pra
  // revogar. "Não se aplica" cobre quem nunca teve.
  { nome: 'E-mail (G-Suite)', aplicavelEm: ['desligamento'], permiteNaoSeAplica: true },
  { nome: 'HelpDesk', aplicavelEm: ['admissao', 'desligamento'], permiteNaoSeAplica: false },
  { nome: 'RaroHub', aplicavelEm: ['admissao', 'desligamento'], permiteNaoSeAplica: false },
  // GitLab saiu da Admissão de propósito (decisão de produto) — não é mais criado por
  // padrão pra todo mundo que entra, só sob demanda (ver tipo 'acessos'). Continua em
  // Desligamento: quem já tinha o acesso (concedido depois, via 'acessos') ainda precisa
  // dele revogado na saída — "Não se aplica" cobre quem nunca teve.
  { nome: 'GitLab', aplicavelEm: ['desligamento'], permiteNaoSeAplica: true },
  { nome: 'EMIVE', aplicavelEm: ['desligamento'], permiteNaoSeAplica: true },
  { nome: 'Cartão de Acesso', aplicavelEm: ['desligamento'], permiteNaoSeAplica: true },
  { nome: 'Chave Casa Raro', aplicavelEm: ['desligamento'], permiteNaoSeAplica: true },
  { nome: 'RaroLogs Teams', aplicavelEm: ['admissao'], permiteNaoSeAplica: false },
  { nome: 'Email boas-vindas', aplicavelEm: ['admissao'], permiteNaoSeAplica: false },
  { nome: 'Email HelpDesk', aplicavelEm: ['admissao'], permiteNaoSeAplica: false },
  { nome: 'Email RaroHub', aplicavelEm: ['admissao'], permiteNaoSeAplica: false },
];

/**
 * Existe algum item do catálogo aplicável a este tipo que ainda não está Concluído/Não se
 * aplica — inclui itens que nunca foram tocados (nem chegaram a ser salvos no array
 * `acessos` da Solicitação, ficam "Pendente" só pelo default visual do AcessosChecklist).
 * Sem considerar esses itens nunca-tocados, dava pra concluir uma Solicitação com acessos
 * mostrando "Pendente" na tela — o array salvo só tinha o que a SI chegou a clicar.
 *
 * Tipo 'acessos' (pedido pontual, fora do catálogo fixo) usa só o que está salvo mesmo,
 * sem catálogo — é sempre 1 item, criado já na abertura da Solicitação.
 */
export function existeAcessoPendente(tipoSolicitacao: TipoSolicitacao, acessos: SolicitacaoAcesso[]): boolean {
  if (tipoSolicitacao === 'acessos') {
    return acessos.some((acesso) => acesso.status === 'pendente');
  }
  const catalogoAplicavel = CATALOGO_ACESSOS.filter((item) => item.aplicavelEm.includes(tipoSolicitacao));
  return catalogoAplicavel.some((item) => {
    const status = acessos.find((acesso) => acesso.sistema === item.nome)?.status ?? 'pendente';
    return status === 'pendente';
  });
}

/**
 * Tipo 'acessos' — pedido pontual de criação/reset de senha de um sistema específico
 * (fora do fluxo padrão de Admissão/Desligamento). Criação nunca inclui Microsoft: quem
 * já acessa o Digital já tem Microsoft (é o próprio login), não faz sentido "criar" de
 * novo. Reset de senha vale pra qualquer um dos 4.
 */
export const SISTEMAS_ACESSO_CRIACAO = ['HelpDesk', 'RaroHub', 'GitLab'] as const;
export const SISTEMAS_ACESSO_RESET_SENHA = ['Microsoft', 'HelpDesk', 'RaroHub', 'GitLab'] as const;

export const ACAO_ACESSO_SOLICITACAO_LABELS: Record<'criar' | 'resetar_senha', string> = {
  criar: 'Criação',
  resetar_senha: 'Reset de Senha',
};

export const TIPO_EQUIPAMENTO_LABELS: Record<TipoEquipamentoMovimentacao, string> = {
  imac: 'iMac',
  macmini: 'Mac Mini',
  macbook: 'Macbook',
  monitor: 'Monitor',
  notebook: 'Notebook',
  periferico: 'Periférico',
  smartphone: 'Smartphone',
  cartao_acesso: 'Cartão de acesso',
  chip: 'Chip (cartão SIM)',
  outros: 'Outros',
};

export const SISTEMA_OPERACIONAL_LABELS: Record<SistemaOperacionalMovimentacao, string> = {
  android: 'Android',
  ios: 'iOS',
  linux_ubuntu: 'Linux (Ubuntu)',
  macos: 'MacOS',
  windows: 'Windows',
  outros: 'Outros',
};

export const PROPRIEDADE_EQUIPAMENTO_LABELS: Record<PropriedadeEquipamento, string> = {
  raro: 'Raro',
  cliente: 'Cliente',
  nao_informado: 'Não sei informar',
};

export const PROPRIEDADE_EQUIPAMENTO_OPCOES: PropriedadeEquipamento[] = ['raro', 'cliente', 'nao_informado'];

/**
 * "Cliente" vira o nome real do cliente (ex. "SWAP") em vez do genérico — só "Raro" e
 * "Não sei informar" são fixos. Só alguns Clientes permitem essa opção
 * (ConfigRegrasCliente.permiteEquipamentoCliente, configurável pelo SI em
 * ConfiguracoesDrawer — não hardcoded).
 */
export function labelPropriedadeEquipamento(valor: PropriedadeEquipamento, clienteNome: string | null): string {
  if (valor === 'cliente') return clienteNome ?? PROPRIEDADE_EQUIPAMENTO_LABELS.cliente;
  return PROPRIEDADE_EQUIPAMENTO_LABELS[valor];
}

/**
 * Sistema Operacional só é perguntado a quem preenche quando o tipo de equipamento não
 * tem um SO implícito — hoje só Smartphone (Notebook é sempre Windows, Macbook/MacMini/
 * iMac são sempre MacOS, por padrão da empresa). Ver `SO_IMPLICITO_POR_TIPO_EQUIPAMENTO`
 * para os tipos que nem perguntam.
 */
export const SO_OPCOES_POR_TIPO_EQUIPAMENTO: Partial<Record<TipoEquipamentoMovimentacao, SistemaOperacionalMovimentacao[]>> = {
  smartphone: ['android', 'ios', 'outros'],
};

/** SO implícito (nunca perguntado) — preenchido automaticamente ao escolher o tipo. */
export const SO_IMPLICITO_POR_TIPO_EQUIPAMENTO: Partial<Record<TipoEquipamentoMovimentacao, SistemaOperacionalMovimentacao>> = {
  notebook: 'windows',
  macbook: 'macos',
  macmini: 'macos',
  imac: 'macos',
};

/** Notebook/Macbook/MacMini/iMac podem ser "homologados" (SO já é sempre Windows/MacOS
 * pra esses tipos, então a condição depende só do tipo, não mais do SO escolhido). */
export const TIPOS_EQUIPAMENTO_COM_HOMOLOGACAO: TipoEquipamentoMovimentacao[] = [
  'notebook',
  'macbook',
  'macmini',
  'imac',
];

/** Só equipamento com login/SO tem senha pra registrar — monitor, periférico e outros nunca têm. */
export const TIPOS_EQUIPAMENTO_COM_SENHA: TipoEquipamentoMovimentacao[] = [
  'notebook',
  'macbook',
  'macmini',
  'imac',
  'smartphone',
];

/**
 * Pergunta invertida pra quem abre a solicitação (NovaSolicitacaoContent): quem pede um
 * equipamento quase sempre sabe o Sistema Operacional que precisa (Windows/macOS/Android/
 * iOS), não o tipo físico exato — pedir o SO primeiro e derivar o tipo é mais natural do
 * que pedir "Notebook, Macbook, Mac Mini ou iMac?" direto. Decisão de produto explícita.
 */
export type SistemaOperacionalPrincipal = 'windows' | 'macos' | 'android' | 'ios';

export const SISTEMA_OPERACIONAL_PRINCIPAL_LABELS: Record<SistemaOperacionalPrincipal, string> = {
  windows: 'Windows',
  macos: 'macOS',
  android: 'Android',
  ios: 'iOS',
};

/**
 * Tipo derivado direto do SO — sem ambiguidade, não precisa de pergunta extra nem trava a
 * abertura da solicitação. macOS fica de fora de propósito: existem 3 tipos possíveis
 * (Macbook/Mac Mini/iMac) e qual deles vai pra mão do colaborador depende do que tem
 * disponível no estoque, não de preferência (decisão de produto — perguntar "notebook ou
 * desktop" não ajudava, todo mundo prefere Macbook). Pra macOS o `tipoEquipamento` fica
 * `null` no intake — o SI escolhe o modelo exato depois, na tela de execução.
 */
export const TIPO_EQUIPAMENTO_POR_SISTEMA_OPERACIONAL_PRINCIPAL: Partial<
  Record<SistemaOperacionalPrincipal, TipoEquipamentoMovimentacao>
> = {
  windows: 'notebook',
  android: 'smartphone',
  ios: 'smartphone',
};

/** Tipos sem sistema operacional — quem abre escolhe o tipo direto (sem pergunta de SO). */
export const TIPOS_EQUIPAMENTO_SEM_SO: TipoEquipamentoMovimentacao[] = ['monitor', 'periferico', 'outros'];

/**
 * Condição do equipamento no momento da Movimentação — opções diferentes por
 * `tipoFluxo` (envio = equipamento saindo da Raro; devolução = equipamento voltando,
 * onde faz sentido avaliar o estado de uso). Usado no Termo gerado (termoPdfService).
 */
export const CONDICAO_EQUIPAMENTO_ENVIO_OPCOES = ['novo', 'usado'] as const;
export const CONDICAO_EQUIPAMENTO_DEVOLUCAO_OPCOES = ['otima', 'boa', 'pessima', 'com_defeito'] as const;

export const CONDICAO_EQUIPAMENTO_LABELS: Record<string, string> = {
  novo: 'Novo',
  usado: 'Usado',
  otima: 'Ótima',
  boa: 'Boa',
  pessima: 'Péssima',
  com_defeito: 'Com defeito',
};

/** Catálogo fixo dos acessórios mais comuns — "Outros" é sempre um campo de texto livre à
 * parte. "nao_inclui" existe pra Acessórios poder ser obrigatório (ver pendenciaMovimentacao)
 * sem travar equipamento que realmente não tem nenhum acessório junto. */
/** "Cartão de acesso" saiu daqui: agora é um item próprio da Movimentação (tipo de
 * equipamento, com patrimônio/termo/Inventário). Registros antigos continuam com o rótulo. */
export const ACESSORIO_OPCOES = [
  'carregador',
  'mouse',
  'case',
  'hdmi',
  'fonte',
  'chave',
  'nao_inclui',
] as const;

export const ACESSORIO_LABELS: Record<(typeof ACESSORIO_OPCOES)[number] | 'cartao_acesso', string> = {
  carregador: 'Carregador',
  mouse: 'Mouse',
  case: 'Case',
  hdmi: 'HDMI',
  fonte: 'Fonte',
  cartao_acesso: 'Cartão de acesso',
  chave: 'Chave',
  nao_inclui: 'Não inclui',
};
